import React, { useState, useEffect, useCallback } from 'react';
import { collection, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { createUserAsAdmin } from '../utils/createUser';
import MapView from './MapView';
import StatCards from './StatCards';
import './AdminScanLocationsView.css';
import './AdminMoreDetailPages.css';
import { docInScope, adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

const LOCATION_CATEGORIES = [
  { value: 'Entrance', icon: '🚪' },
  { value: 'Exit', icon: '🚪' },
  { value: 'Transport', icon: '🚌' },
  { value: 'Classroom', icon: '🏫' },
  { value: 'Library', icon: '📚' },
  { value: 'Cafeteria', icon: '🍽️' },
  { value: 'Activity Area', icon: '⚽' },
  { value: 'Club/Programme', icon: '👥' },
  { value: 'Event', icon: '🎉' },
  { value: 'Office', icon: '🏢' },
  { value: 'Other', icon: '📍' },
];

function AdminScanLocationsView({ adminData }) {
  const [activeSection, setActiveSection] = useState('locations');

  // --- Scan Locations state (existing) ---
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [error, setError] = useState('');
  const [newLocation, setNewLocation] = useState({
    name: '',
    category: 'Entrance',
    latitude: '',
    longitude: ''
  });
  const [editForm, setEditForm] = useState({
    name: '',
    category: 'Entrance',
    isActive: true,
    latitude: '',
    longitude: ''
  });

  // --- Scan Activity state (new) ---
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityDateFilter, setActivityDateFilter] = useState('today');
  const [activityTypeFilter, setActivityTypeFilter] = useState('all');

  // --- Student Reports state (new) ---
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportStatusFilter, setReportStatusFilter] = useState('all');
  const [resolvingId, setResolvingId] = useState(null);

  // --- Scanner accounts state ---
  const [scanners, setScanners] = useState([]);
  const [scannersLoading, setScannersLoading] = useState(false);
  const [showAddScanner, setShowAddScanner] = useState(false);
  const [scannerForm, setScannerForm] = useState({ firstName: '', lastName: '', email: '', password: '' });
  const [scannerError, setScannerError] = useState('');
  const [scannerSaving, setScannerSaving] = useState(false);

  const loadLocations = useCallback(async () => {
    setLoading(true);
    try {
      const schoolId = adminData?.schoolId;
      if (!schoolId) {
        setError('No school assigned. Contact your Super Admin.');
        setLoading(false);
        return;
      }
      const docs = await dataCacheService.getDocs(`${schoolId.toLowerCase()}:scanLocations`, collection(db, 'scanLocations'));
      setLocations(docs.filter(d => docInScope(d.schoolId, adminSchoolIds(adminData))));
    } catch (err) {
      console.error('Error loading scan locations:', err);
    } finally {
      setLoading(false);
    }
  }, [adminData?.schoolId]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  // --- Activity logs loading ---
  const tsToMs = (ts) => {
    if (!ts) return 0;
    if (ts.seconds) return ts.seconds * 1000;
    if (ts.toMillis) return ts.toMillis();
    if (ts instanceof Date) return ts.getTime();
    return new Date(ts).getTime() || 0;
  };

  const loadActivityLogs = useCallback(async () => {
    if (!adminData?.schoolId) return;
    setActivityLoading(true);
    try {
      const allLogs = await dataCacheService.getDocs(`${adminData.schoolId.toLowerCase()}:studentActivityLogs`, collection(db, 'studentActivityLogs'));
      let logs = allLogs.filter(d => docInScope(d.schoolId, adminSchoolIds(adminData)));
      logs.sort((a, b) => tsToMs(b.timestamp) - tsToMs(a.timestamp));
      setActivityLogs(logs);
    } catch (e) {
      console.error('Error loading activity logs:', e);
    } finally {
      setActivityLoading(false);
    }
  }, [adminData?.schoolId]);

  useEffect(() => {
    loadActivityLogs();
  }, [activeSection, loadActivityLogs]);

  // --- Reports loading ---
  const loadReports = useCallback(async () => {
    if (!adminData?.schoolId) return;
    setReportsLoading(true);
    try {
      const allReports = await dataCacheService.getDocs(`${adminData.schoolId.toLowerCase()}:studentReports`, collection(db, 'studentReports'));
      let r = allReports.filter(d => docInScope(d.schoolId, adminSchoolIds(adminData)));
      r.sort((a, b) => tsToMs(b.timestamp) - tsToMs(a.timestamp));
      setReports(r);
    } catch (e) {
      console.error('Error loading reports:', e);
    } finally {
      setReportsLoading(false);
    }
  }, [adminData?.schoolId]);

  useEffect(() => {
    loadReports();
  }, [activeSection, loadReports]);

  // --- Scanner accounts: users with role Scanner at this school ---
  const loadScanners = useCallback(async () => {
    if (!adminData?.schoolId) return;
    setScannersLoading(true);
    try {
      const ids = adminSchoolIds(adminData);
      const allUsers = await dataCacheService.getDocs(`${adminData.schoolId.toLowerCase()}:users`, collection(db, 'users'));
      setScanners(allUsers
        .filter(u => String(u.role || '').toLowerCase() === 'scanner' && docInScope(u.schoolId, ids)));
    } catch (e) {
      console.error('Error loading scanners:', e);
    } finally {
      setScannersLoading(false);
    }
  }, [adminData]);

  useEffect(() => {
    if (activeSection === 'scanners') loadScanners();
  }, [activeSection, loadScanners]);

  const handleAddScanner = async (e) => {
    e.preventDefault();
    setScannerError('');
    if (!scannerForm.firstName.trim() || !scannerForm.lastName.trim() || !scannerForm.email.trim() || !scannerForm.password) {
      setScannerError('All fields are required');
      return;
    }
    setScannerSaving(true);
    try {
      const uid = await createUserAsAdmin(scannerForm.email.trim(), scannerForm.password);
      await setDoc(doc(db, 'users', uid), {
        id: uid,
        uuid: crypto.randomUUID(),
        username: `${scannerForm.firstName.trim()} ${scannerForm.lastName.trim()}`,
        firstName: scannerForm.firstName.trim(),
        lastName: scannerForm.lastName.trim(),
        email: scannerForm.email.trim(),
        role: 'Scanner',
        assignedClassIds: [],
        specialtySubjects: [],
        schoolId: adminData.schoolId,
        isActive: true,
        createdAt: new Date().toISOString(),
      });
      setScannerForm({ firstName: '', lastName: '', email: '', password: '' });
      setShowAddScanner(false);
      dataCacheService.invalidatePath('users');
      loadScanners();
    } catch (err) {
      console.error('Error creating scanner:', err);
      setScannerError(err.message || 'Failed to create scanner account');
    } finally {
      setScannerSaving(false);
    }
  };

  const handleResolveReport = async (reportId) => {
    setResolvingId(reportId);
    try {
      await updateDoc(doc(db, 'studentReports', reportId), {
        status: 'Resolved',
        resolvedAt: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
      });
      dataCacheService.invalidatePath('studentReports');
      setReports(prev => prev.map(r => r.id === reportId
        ? { ...r, status: 'Resolved' } : r));
    } catch (e) {
      console.error('Error resolving report:', e);
      alert('Failed to resolve report.');
    } finally {
      setResolvingId(null);
    }
  };

  // --- Location handlers (existing, unchanged) ---
  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!newLocation.name.trim()) {
      setError('Location name is required');
      return;
    }
    try {
      const locationId = crypto.randomUUID();
      const latitude = newLocation.latitude !== '' ? parseFloat(newLocation.latitude) : 0;
      const longitude = newLocation.longitude !== '' ? parseFloat(newLocation.longitude) : 0;
      const locationData = {
        id: locationId,
        schoolId: adminData.schoolId,
        name: newLocation.name.trim(),
        category: newLocation.category,
        isActive: true,
        latitude,
        longitude,
        createdBy: adminData?.uid || '',
        createdAt: new Date().toISOString()
      };
      await setDoc(doc(db, 'scanLocations', locationId), locationData);
      dataCacheService.invalidatePath('scanLocations');
      setLocations([...locations, locationData]);
      setNewLocation({ name: '', category: 'Entrance', latitude: '', longitude: '' });
      setShowAddForm(false);
      setError('');
    } catch (err) {
      console.error('Error adding location:', err);
      setError('Failed to add location');
    }
  };

  const handleEditClick = (location) => {
    setEditingLocation(location);
    setEditForm({
      name: location.name,
      category: location.category || 'Other',
      isActive: location.isActive !== false,
      latitude: location.latitude !== undefined ? String(location.latitude) : '',
      longitude: location.longitude !== undefined ? String(location.longitude) : ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingLocation) return;
    try {
      const latitude = editForm.latitude !== '' ? parseFloat(editForm.latitude) : 0;
      const longitude = editForm.longitude !== '' ? parseFloat(editForm.longitude) : 0;
      await updateDoc(doc(db, 'scanLocations', editingLocation.id), {
        name: editForm.name.trim(),
        category: editForm.category,
        isActive: editForm.isActive,
        latitude,
        longitude
      });
      dataCacheService.invalidatePath('scanLocations');
      setLocations(locations.map(l =>
        l.id === editingLocation.id
          ? { ...l, name: editForm.name.trim(), category: editForm.category, isActive: editForm.isActive, latitude, longitude }
          : l
      ));
      setEditingLocation(null);
      setError('');
    } catch (err) {
      console.error('Error updating location:', err);
      setError('Failed to update location');
    }
  };

  const handleToggleActive = async (location) => {
    try {
      const newActive = !location.isActive;
      await updateDoc(doc(db, 'scanLocations', location.id), { isActive: newActive });
      dataCacheService.invalidatePath('scanLocations');
      setLocations(locations.map(l =>
        l.id === location.id ? { ...l, isActive: newActive } : l
      ));
    } catch (err) {
      console.error('Error toggling location:', err);
    }
  };

  const handleDeleteLocation = async (location) => {
    if (!confirm(`Delete "${location.name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'scanLocations', location.id));
      dataCacheService.invalidatePath('scanLocations');
      setLocations(locations.filter(l => l.id !== location.id));
    } catch (err) {
      console.error('Error deleting location:', err);
      setError('Failed to delete location');
    }
  };

  const getCategoryIcon = (category) => {
    const cat = LOCATION_CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : '📍';
  };

  // --- Activity helpers ---
  const startOfToday = () => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  };
  const startOfWeek = () => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    return d;
  };

  const filteredActivity = activityLogs.filter((log) => {
    const ms = tsToMs(log.timestamp);
    if (activityDateFilter === 'today' && ms < startOfToday().getTime()) return false;
    if (activityDateFilter === 'week' && ms < startOfWeek().getTime()) return false;
    if (activityTypeFilter !== 'all' && log.activityType !== activityTypeFilter) return false;
    return true;
  });

  const scansToday = activityLogs.filter(l => tsToMs(l.timestamp) >= startOfToday().getTime()).length;
  const scansThisWeek = activityLogs.filter(l => tsToMs(l.timestamp) >= startOfWeek().getTime()).length;
  const activityTypes = Array.from(new Set(activityLogs.map(l => l.activityType).filter(Boolean)));

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    const ms = tsToMs(ts);
    if (!ms) return '—';
    return new Date(ms).toLocaleString();
  };

  const filteredReports = reports.filter(r =>
    reportStatusFilter === 'all' || String(r.status || 'pending').toLowerCase() === reportStatusFilter
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-scan-locations-view">
      <div className="scanner-sub-tabs">
        <button
          className={`sub-tab ${activeSection === 'locations' ? 'active' : ''}`}
          onClick={() => setActiveSection('locations')}
        >📍 Locations</button>
        <button
          className={`sub-tab ${activeSection === 'scanners' ? 'active' : ''}`}
          onClick={() => setActiveSection('scanners')}
        >🔍 Scanners</button>
        <button
          className={`sub-tab ${activeSection === 'activity' ? 'active' : ''}`}
          onClick={() => setActiveSection('activity')}
        >📋 Activity</button>
        <button
          className={`sub-tab ${activeSection === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveSection('reports')}
        >🚨 Reports</button>
      </div>
      <StatCards items={[
        ['Total Scans', activityLogs.length], ['Unique Students', new Set(activityLogs.map(l => l.studentId).filter(Boolean)).size],
        ['Unique Scanners', new Set(activityLogs.map(l => l.scannerId).filter(Boolean)).size], ['Activity Types', activityTypes.length],
        ['Locations', locations.length], ["Today's Scans", scansToday],
        ['Total Reports', reports.length], ['Open', reports.filter(r => String(r.status || 'open').toLowerCase() !== 'resolved').length], ['Resolved', reports.filter(r => String(r.status || '').toLowerCase() === 'resolved').length]
      ]} />

      {/* === Section 1: Scan Locations === */}
      {activeSection === 'locations' && (
        <div className="scanner-section">
          {selectedLocation ? (
            <div className="detail-page">
              {(() => {
                const loc = selectedLocation;
                const locScans = activityLogs.filter(l => l.locationId === loc.id || l.locationName === loc.name);
                const locScansToday = locScans.filter(l => tsToMs(l.timestamp) >= startOfToday().getTime()).length;
                console.log('🔥 AdminScanLocations: Showing detail for location', loc.id, loc);
                return (
                  <>
                    <button className="detail-back-button" onClick={() => setSelectedLocation(null)}>
                      ◄ Back to Scan Locations
                    </button>
                    <div className="detail-card">
                      <div className="detail-header">
                        <div>
                          <h2>{getCategoryIcon(loc.category)} {loc.name}</h2>
                          <p className="detail-subtitle">{loc.category || 'Location'}</p>
                        </div>
                        <span className="detail-status-badge" style={{
                          background: loc.isActive !== false ? '#d1fae5' : '#f3f4f6',
                          color: loc.isActive !== false ? '#065f46' : '#6b7280'
                        }}>
                          {loc.isActive !== false ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <div className="detail-grid">
                        <div className="detail-field">
                          <span className="detail-label">Name</span>
                          <span className="detail-value">{loc.name}</span>
                        </div>
                        <div className="detail-field">
                          <span className="detail-label">Category</span>
                          <span className="detail-value">{loc.category || 'N/A'}</span>
                        </div>
                        <div className="detail-field">
                          <span className="detail-label">Latitude</span>
                          <span className="detail-value">{loc.latitude ?? '—'}</span>
                        </div>
                        <div className="detail-field">
                          <span className="detail-label">Longitude</span>
                          <span className="detail-value">{loc.longitude ?? '—'}</span>
                        </div>
                        <div className="detail-field">
                          <span className="detail-label">Total Scans</span>
                          <span className="detail-value">{locScans.length}</span>
                        </div>
                        <div className="detail-field">
                          <span className="detail-label">Scans Today</span>
                          <span className="detail-value">{locScansToday}</span>
                        </div>
                        <div className="detail-field">
                          <span className="detail-label">Created</span>
                          <span className="detail-value">{loc.createdAt ? new Date(loc.createdAt).toLocaleDateString() : 'N/A'}</span>
                        </div>
                        <div className="detail-field">
                          <span className="detail-label">Created By</span>
                          <span className="detail-value">{loc.createdBy || 'N/A'}</span>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h4>Recent Scans at This Location</h4>
                        {locScans.length === 0 ? (
                          <p className="detail-empty-text">No scans recorded at this location yet.</p>
                        ) : (
                          <div className="table-wrap">
                          <table className="detail-table">
                            <thead>
                              <tr>
                                <th>Student</th>
                                <th>Activity Type</th>
                                <th>Scanner</th>
                                <th>Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {locScans.slice(0, 25).map(l => (
                                <tr key={l.id}>
                                  <td>{l.studentName || '—'}</td>
                                  <td>{l.activityType || '—'}</td>
                                  <td>{l.scannerName || '—'}</td>
                                  <td>{formatTimestamp(l.timestamp)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        )}
                        {locScans.length > 25 && (
                          <p className="detail-empty-text">Showing 25 of {locScans.length} scans.</p>
                        )}
                      </div>

                      <div className="detail-actions">
                        <button className="detail-btn-primary" onClick={() => handleEditClick(loc)}>
                          ✏️ Edit Location
                        </button>
                        <button
                          className="detail-btn-neutral"
                          onClick={() => { const na = !loc.isActive; handleToggleActive(loc); setSelectedLocation({ ...loc, isActive: na }); }}
                        >
                          {loc.isActive !== false ? '⏸️ Deactivate' : '✅ Activate'}
                        </button>
                        <button className="detail-btn-danger" onClick={async () => { await handleDeleteLocation(loc); setSelectedLocation(null); }}>
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (<>
          <div className="view-header">
            <h2>Scan Locations</h2>
            <button className="add-button" onClick={() => setShowAddForm(!showAddForm)}>
              {showAddForm ? 'Cancel' : '+ Add Location'}
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="map-section">
            <MapView
              markers={locations
                .filter(l => Number.isFinite(l.latitude) && Number.isFinite(l.longitude) && (l.latitude !== 0 || l.longitude !== 0))
                .map(l => ({
                  lat: l.latitude,
                  lng: l.longitude,
                  color: l.isActive !== false ? '#22c55e' : '#9ca3af',
                  label: `<div><strong>${l.name}</strong><br/>${l.category}<br/>${l.isActive !== false ? 'Active' : 'Inactive'}</div>`
                }))}
              height={480}
              defaultZoom={14}
            />
          </div>

          {showAddForm && (
            <div className="add-location-form">
              <h3>Create New Location</h3>
              <form onSubmit={handleAddLocation}>
                <div className="form-group">
                  <label>Location Name *</label>
                  <input
                    type="text"
                    value={newLocation.name}
                    onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                    placeholder="e.g., School Entrance, Bus Bay, Library"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={newLocation.category}
                    onChange={(e) => setNewLocation({ ...newLocation, category: e.target.value })}
                  >
                    {LOCATION_CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.icon} {cat.value}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group half">
                  <label>Latitude</label>
                  <input
                    type="number"
                    step="any"
                    value={newLocation.latitude}
                    onChange={(e) => setNewLocation({ ...newLocation, latitude: e.target.value })}
                    placeholder="17.3000"
                  />
                </div>
                <div className="form-group half">
                  <label>Longitude</label>
                  <input
                    type="number"
                    step="any"
                    value={newLocation.longitude}
                    onChange={(e) => setNewLocation({ ...newLocation, longitude: e.target.value })}
                    placeholder="-62.7000"
                  />
                </div>
                <div className="form-actions">
                  <button type="button" onClick={() => setShowAddForm(false)}>Cancel</button>
                  <button type="submit">Add Location</button>
                </div>
              </form>
            </div>
          )}

          {locations.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📍</div>
              <h3>No Scan Locations</h3>
              <p>Add locations like School Entrance, Bus Bay, Library, etc. These will be available to scanners on the iOS app.</p>
            </div>
          ) : (
            <div className="locations-list">
              {locations.map(location => (
                <div
                  key={location.id}
                  className="location-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedLocation(location)}
                >
                  <div className="location-icon">{getCategoryIcon(location.category)}</div>
                  <div className="location-info">
                    <h4>{location.name}</h4>
                    <span className="location-category">{location.category}</span>
                    {location.isActive === false && <span className="inactive-badge">Inactive</span>}
                  </div>
                  <div className="location-actions">
                    <button
                      className="toggle-button"
                      onClick={(e) => { e.stopPropagation(); handleToggleActive(location); }}
                      title={location.isActive !== false ? 'Deactivate' : 'Activate'}
                    >
                      {location.isActive !== false ? '✅ Active' : '⏸️ Inactive'}
                    </button>
                    <button className="edit-button" onClick={(e) => { e.stopPropagation(); handleEditClick(location); }}>
                      ✏️ Edit
                    </button>
                    <button className="delete-button" onClick={(e) => { e.stopPropagation(); handleDeleteLocation(location); }}>
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          </>)}

          {editingLocation && (
            <div className="modal-overlay" onClick={() => setEditingLocation(null)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Edit Location</h3>
                <div className="form-group">
                  <label>Location Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  >
                    {LOCATION_CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.icon} {cat.value}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group half">
                  <label>Latitude</label>
                  <input
                    type="number"
                    step="any"
                    value={editForm.latitude}
                    onChange={(e) => setEditForm({ ...editForm, latitude: e.target.value })}
                  />
                </div>
                <div className="form-group half">
                  <label>Longitude</label>
                  <input
                    type="number"
                    step="any"
                    value={editForm.longitude}
                    onChange={(e) => setEditForm({ ...editForm, longitude: e.target.value })}
                  />
                </div>
                <div className="form-group toggle">
                  <label>
                    <input
                      type="checkbox"
                      checked={editForm.isActive}
                      onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
                <div className="modal-actions">
                  <button className="cancel-button" onClick={() => setEditingLocation(null)}>Cancel</button>
                  <button className="save-button" onClick={handleSaveEdit}>Save</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* === Section 2: Scanner Accounts === */}
      {activeSection === 'scanners' && (
        <div className="scanner-section">
          <div className="view-header">
            <h2>Scanner Accounts</h2>
            <button className="add-button" onClick={() => setShowAddScanner(!showAddScanner)}>
              {showAddScanner ? 'Cancel' : '+ Add Scanner'}
            </button>
          </div>

          <div className="info-banner">
            ℹ️ Scanner accounts sign in to the scanner app and are linked to your school automatically.
          </div>

          {scannerError && <div className="error-message">{scannerError}</div>}

          {showAddScanner && (
            <form className="add-location-form" onSubmit={handleAddScanner}>
              <h3>Create Scanner Account</h3>
              <div className="form-group half">
                <label>First Name *</label>
                <input type="text" value={scannerForm.firstName}
                  onChange={e => setScannerForm({ ...scannerForm, firstName: e.target.value })} required />
              </div>
              <div className="form-group half">
                <label>Last Name *</label>
                <input type="text" value={scannerForm.lastName}
                  onChange={e => setScannerForm({ ...scannerForm, lastName: e.target.value })} required />
              </div>
              <div className="form-group half">
                <label>Email *</label>
                <input type="email" value={scannerForm.email}
                  onChange={e => setScannerForm({ ...scannerForm, email: e.target.value })} required />
              </div>
              <div className="form-group half">
                <label>Password *</label>
                <input type="password" value={scannerForm.password}
                  onChange={e => setScannerForm({ ...scannerForm, password: e.target.value })} required />
              </div>
              <button type="submit" className="detail-btn-primary" disabled={scannerSaving}>
                {scannerSaving ? 'Creating…' : 'Create Scanner'}
              </button>
            </form>
          )}

          {scannersLoading ? (
            <div className="scanner-empty">Loading scanners…</div>
          ) : scanners.length === 0 ? (
            <div className="scanner-empty">No scanner accounts yet — create one so staff can scan student IDs.</div>
          ) : (
            <div className="table-wrap">
            <table className="activity-table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Scans</th></tr>
              </thead>
              <tbody>
                {scanners.map(s => (
                  <tr key={s.id}>
                    <td>{s.firstName || s.lastName ? `${s.firstName || ''} ${s.lastName || ''}`.trim() : (s.username || '—')}</td>
                    <td>{s.email || '—'}</td>
                    <td>{activityLogs.filter(l => l.scannerId === s.id).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {/* === Section 3: Scan Activity === */}
      {activeSection === 'activity' && (
        <div className="scanner-section">
          <h2>Scan Activity</h2>

          <div className="activity-summary">
            <div className="summary-stat">
              <span className="stat-value">{scansToday}</span>
              <span className="stat-label">Scans Today</span>
            </div>
            <div className="summary-stat">
              <span className="stat-value">{scansThisWeek}</span>
              <span className="stat-label">This Week</span>
            </div>
          </div>

          <div className="activity-filters">
            <select value={activityDateFilter} onChange={e => setActivityDateFilter(e.target.value)}>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="all">All Time</option>
            </select>
            <select value={activityTypeFilter} onChange={e => setActivityTypeFilter(e.target.value)}>
              <option value="all">All Types</option>
              {activityTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {activityLoading ? (
            <div className="scanner-empty">Loading activity…</div>
          ) : filteredActivity.length === 0 ? (
            <div className="scanner-empty">No scan activity found.</div>
          ) : (
            <div className="table-wrap">
            <table className="activity-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Scanner</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {filteredActivity.map(log => (
                  <tr key={log.id}>
                    <td>{log.studentName || '—'}</td>
                    <td><span className="activity-badge">{log.activityType || '—'}</span></td>
                    <td>{log.locationName || '—'}</td>
                    <td>{log.scannerName || '—'}</td>
                    <td>{formatTimestamp(log.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {/* === Section 4: Student Reports === */}
      {activeSection === 'reports' && (
        <div className="scanner-section">
          <h2>Student Reports</h2>

          <div className="reports-filters">
            <select value={reportStatusFilter} onChange={e => setReportStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>

          {reportsLoading ? (
            <div className="scanner-empty">Loading reports…</div>
          ) : filteredReports.length === 0 ? (
            <div className="scanner-empty">No student reports found.</div>
          ) : (
            <div className="reports-grid">
              {filteredReports.map(r => (
                <div key={r.id} className={`report-card ${r.status || 'pending'}`}>
                  <div className="report-header">
                    <span className="report-student">{r.studentName || 'Unknown student'}</span>
                    <span className={`report-status-badge ${r.status || 'pending'}`}>{r.status || 'pending'}</span>
                  </div>
                  <div className="report-type">{r.reportType || 'Report'}</div>
                  {r.description && <p className="report-desc">{r.description}</p>}
                  <div className="report-meta">
                    <span>By: {r.scannerName || r.reportedBy || '—'}</span>
                    <span>{formatTimestamp(r.timestamp)}</span>
                  </div>
                  {String(r.status || '').toLowerCase() !== 'resolved' && (
                    <button
                      className="resolve-btn"
                      disabled={resolvingId === r.id}
                      onClick={() => handleResolveReport(r.id)}
                    >{resolvingId === r.id ? 'Resolving…' : 'Resolve'}</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminScanLocationsView;
